import * as Cesium from 'cesium';
import * as satellite from 'satellite.js';
import { OrbitParams } from '../types/mission';
import {
  meanMotionRadPerSec,
  solveEccentricAnomaly,
  eccentricToTrueAnomaly,
  keplerianToECI,
  DEG2RAD,
} from '../../../modelling/utils/orbitCalculations';

const R_EARTH = 6371;           // km
const MAX_KEPLERIAN_SAMPLES = 4_000;

export function buildSampledPosition(orbit: OrbitParams): any {
  const { altKm, incDeg, raanDeg, M0Deg } = orbit;
  const r = R_EARTH + altKm;
  const MU = 398600.4418;
  const T = 2 * Math.PI * Math.sqrt(Math.pow(r, 3) / MU); // orbital period (s)

  const incRad = (incDeg * Math.PI) / 180;
  const raanRad = (raanDeg * Math.PI) / 180;

  const prop = new Cesium.SampledPositionProperty(Cesium.ReferenceFrame.INERTIAL);
  prop.setInterpolationOptions({
    interpolationAlgorithm: Cesium.LagrangePolynomialApproximation,
    interpolationDegree: 5,
  });

  const start = Cesium.JulianDate.now();
  const SAMPLES = 200;
  const span = 2 * T;

  for (let i = 0; i <= SAMPLES; i++) {
    const t = (i / SAMPLES) * span;
    const M = (M0Deg * Math.PI) / 180 + (2 * Math.PI / T) * t;

    const rKm = r;
    const xPeri = rKm * Math.cos(M);
    const yPeri = rKm * Math.sin(M);

    const xI = xPeri;
    const yI = yPeri * Math.cos(incRad);
    const zI = yPeri * Math.sin(incRad);

    const xECI = xI * Math.cos(raanRad) - yI * Math.sin(raanRad);
    const yECI = xI * Math.sin(raanRad) + yI * Math.cos(raanRad);
    const zECI = zI;

    const pos = new Cesium.Cartesian3(xECI * 1000, yECI * 1000, zECI * 1000);
    const jd = Cesium.JulianDate.addSeconds(start, t, new Cesium.JulianDate());
    prop.addSample(jd, pos);
  }

  return prop;
}

// ─── Full Keplerian propagator ────────────────────────────────────────────────

export interface KeplerianOrbitParams {
  semiMajorAxis: number; // km
  eccentricity: number;
  inclination: number;   // degrees
  RAAN: number;          // degrees
  AOP: number;           // degrees
  TA: number;            // true anomaly at epoch, degrees
  epoch: number;         // Unix seconds (0 or null → use startDate as epoch)
}

/**
 * Build a Cesium.SampledPositionProperty in the INERTIAL (ECI) reference frame
 * using full Keplerian elements. Cesium automatically converts ECI → ECEF each
 * render frame, so orbits precess correctly on the rotating globe.
 *
 * Positions are sampled over `orbitTurns` orbital periods starting from `startDate`.
 * The satellite's position at `startDate` is computed by propagating from the epoch
 * using Kepler's equation.
 */
export function buildKeplerianSampledPosition(
  params: KeplerianOrbitParams,
  startDate: Date,
  orbitTurns = 1.5,
  samples = 360,
): Cesium.SampledPositionProperty {
  const { semiMajorAxis, eccentricity, inclination, RAAN, AOP, TA } = params;

  // Fall back to startDate as epoch when epoch is absent / zero
  const epochMs = params.epoch ? params.epoch * 1000 : startDate.getTime();

  const n = meanMotionRadPerSec(semiMajorAxis);
  const T = (2 * Math.PI) / n;           // orbital period in seconds
  const totalSpanSec = T * orbitTurns;

  // TA@epoch → E₀ (via half-angle substitution) → M₀
  const ta0 = TA * DEG2RAD;
  const sqrtTerm = Math.sqrt((1 - eccentricity) / (1 + eccentricity));
  const E0 = 2 * Math.atan2(sqrtTerm * Math.sin(ta0 / 2), Math.cos(ta0 / 2));
  const M0 = E0 - eccentricity * Math.sin(E0);

  // Elapsed seconds from epoch to startDate
  const dt = (startDate.getTime() - epochMs) / 1000;

  const inc_rad = inclination * DEG2RAD;
  const raan_rad = RAAN * DEG2RAD;
  const aop_rad = AOP * DEG2RAD;

  // INERTIAL frame (ECI/ICRF)
  const prop = new Cesium.SampledPositionProperty(Cesium.ReferenceFrame.INERTIAL);
  prop.setInterpolationOptions({
    interpolationAlgorithm: Cesium.LagrangePolynomialApproximation,
    interpolationDegree: 5,
  });

  const startJD = Cesium.JulianDate.fromDate(startDate);

  // Sample every 10s for dense, sub-meter accuracy curve interpolation
  const denseSamples = Math.min(
    MAX_KEPLERIAN_SAMPLES,
    Math.max(samples, Math.round(totalSpanSec / 10)),
  );
  for (let i = 0; i <= denseSamples; i++) {
    const t = (i / denseSamples) * totalSpanSec;
    // Mean anomaly at (epoch + dt + t)
    const M = ((M0 + n * (dt + t)) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    const E = solveEccentricAnomaly(M, eccentricity);
    const nu = eccentricToTrueAnomaly(E, eccentricity);
    const pos = keplerianToECI(semiMajorAxis, eccentricity, inc_rad, raan_rad, aop_rad, nu);
    const jd = Cesium.JulianDate.addSeconds(startJD, t, new Cesium.JulianDate());

    // Add ECI meters directly to property
    prop.addSample(jd, new Cesium.Cartesian3(pos.x, pos.y, pos.z));
  }

  return prop;
}
